import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLoader } from '../../../helpers/useLoader';
import { ACDLError } from '../../../models/error.model';
import { NotificationLevel, ToastNotification } from '../../notifications/models/notification.model';
import { INotificationsService } from '../../notifications/services/notifications.service';
import { IUserService } from '../../user/user.service';
import { ListViewMode } from '../../user/user.store';
import { DatasetDetailsImageListControls } from '../components/DatasetDetailsImageListControls';
import { DatasetDetails } from '../DatasetDetails.context';
import { Datasets } from '../datasets.context';

export const DatasetDetailsImageListControlsContainer = observer((): ReactElement => {
  const userService = useInjection(IUserService.$);
  const notificationsService = useInjection(INotificationsService.$);

  const datasetDetailsContext = useContext(DatasetDetails);
  const datasetsContext = useContext(Datasets);
  const { withLoaderAsync } = useLoader();
  const { datasetId } = useParams<{ datasetId: string }>();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  useEffect(() => {
    datasetDetailsContext.store.selectedImages.clear();
  }, []);

  const handleOrderByChange = async (orderBy: string): Promise<void> => {
    datasetDetailsContext.store.detailsImagesPaging.orderBy = orderBy;
    await doSort();
  };

  const handleOrderDirectionChange = async (orderType: string): Promise<void> => {
    datasetDetailsContext.store.detailsImagesPaging.orderType = orderType;
    await doSort();
  };

  const handleViewModeChange = async (mode: ListViewMode): Promise<void> => {
    await userService.setDatasetImageListViewMode(mode);
  };

  const doSort = async (): Promise<void> => {
    const { pageSize, pageNumber, orderBy, orderType } = datasetDetailsContext.store.detailsImagesPaging;
    await withLoaderAsync(async () => {
      await datasetDetailsContext.service.getDatasetImagesAsync(datasetId, pageNumber, pageSize, orderBy, orderType);
    });
  };

  const handleBulkDelete = async (): Promise<void> => {
    toggleConfirmationModal();
    const { store, service } = datasetDetailsContext;
    await service.bulkDeleteImages(
      store.details.id,
      store.selectedImages.map((si) => si.id)
    );
    datasetsContext.store.refreshFromDetails(store.details);
  };

  const toggleConfirmationModal = (): void => {
    setShowDeleteConfirmation((prevState) => !prevState);
  };

  const handleDownloadImages = async (): Promise<void> => {
    const selectedImages = datasetDetailsContext.store.selectedImages;

    if (selectedImages.length === 0) {
      return;
    }

    const name = datasetDetailsContext.store.detailsImages.filter((i) => i.id === selectedImages[0].id).pop()?.name;

    if (selectedImages.length === 1 && name && datasetDetailsContext.service.isValidFileName(name)) {
      const result = await datasetDetailsContext.service.downloadImageAsync(selectedImages[0].id);
      if (result instanceof ACDLError) {
        await notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'cant_download_image'));
      }
    } else {
      await datasetDetailsContext.service.downloadSelectedImagesAsync(
        datasetId,
        selectedImages.map((si) => si.id)
      );
    }
  };

  const store = datasetDetailsContext.service.store;
  const selectedImagesCount = store.selectedImages.length;
  const usedImagesCount = store.selectedImages.filter((si) => si.hasAnnotation || si.hasLock).length || 0;

  return (
    <DatasetDetailsImageListControls
      selectedImagesCount={selectedImagesCount}
      usedImagesCount={usedImagesCount}
      viewMode={userService.data.datasetImageListViewMode || ListViewMode.List}
      orderBy={store.detailsImagesPaging.orderBy || ''}
      orderDirection={store.detailsImagesPaging.orderType || ''}
      showBulkDeleteConfirmation={showDeleteConfirmation}
      onViewModeChange={handleViewModeChange}
      onOrderByChange={handleOrderByChange}
      onOrderDirectionChange={handleOrderDirectionChange}
      onBulkDelete={handleBulkDelete}
      toggleConfirmationModal={toggleConfirmationModal}
      downloadImages={handleDownloadImages}
      datasetCreatorId={store.details.createdById}
      datasetStatus={store.details.status}
    />
  );
});
